import { useEffect, useState } from "react";
import { Printer, Plus, Trash2, Loader2, Wifi, WifiOff, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";

export default function PrintersSection({ canManage }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", host: "", port: 9100, is_default: false, note: "" });
  const [creating, setCreating] = useState(false);
  const [testingId, setTestingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/printers");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error(t("printer.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.host.trim()) return;
    setCreating(true);
    try {
      await api.post("/printers", { ...form, port: Number(form.port) || 9100 });
      setForm({ name: "", host: "", port: 9100, is_default: false, note: "" });
      toast.success(t("printer.created"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t("printer.createFailed"));
    } finally {
      setCreating(false);
    }
  };

  const setDefault = async (id) => {
    try {
      await api.patch(`/printers/${id}`, { is_default: true });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t("printer.updateFailed"));
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(t("printer.confirmDelete").replace("{name}", name))) return;
    try {
      await api.delete(`/printers/${id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t("printer.deleteFailed"));
    }
  };

  const test = async (id) => {
    setTestingId(id);
    try {
      const r = await api.post(`/printers/${id}/test`);
      if (r.data?.ok) toast.success(t("printer.testOk"));
      else toast.error(t("printer.testFail").replace("{err}", r.data?.error || ""));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t("printer.testFail").replace("{err}", ""));
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="printers-section">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tech inline-flex items-center gap-2">
          <Printer size={14} /> {t("printer.title")}
        </div>
        {items.length > 0 && (
          <div className="label-tech text-[var(--ink-500)]">{items.length}</div>
        )}
      </div>
      <p className="text-sm text-[var(--ink-500)] leading-relaxed mb-4">{t("printer.subtitle")}</p>

      {loading && <div className="label-tech">{t("detail.loading")}</div>}

      {!loading && items.length === 0 && (
        <div className="label-tech text-[var(--ink-500)] py-3" data-testid="printers-empty">
          {t("printer.empty")}
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="divide-y divide-[var(--ink-200)]">
          {items.map((p) => (
            <div key={p.id} className="py-3 flex flex-wrap items-center gap-4" data-testid={`printer-row-${p.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-display text-base flex items-center gap-2">
                  {p.name}
                  {p.is_default && (
                    <span className="tag tag-moss inline-flex items-center gap-1" data-testid={`printer-default-${p.id}`}>
                      <Star size={10} /> {t("printer.default")}
                    </span>
                  )}
                </div>
                <div className="label-tech text-[var(--ink-500)]">{p.host}:{p.port} · {p.type}</div>
                {p.note && <div className="label-tech text-[var(--ink-500)]">{p.note}</div>}
              </div>
              {canManage && (
                <div className="flex flex-wrap gap-2">
                  <button
                    className="btn-ghost"
                    onClick={() => test(p.id)}
                    disabled={testingId === p.id}
                    data-testid={`printer-test-${p.id}`}
                  >
                    {testingId === p.id ? <Loader2 size={12} className="animate-spin" /> : <Wifi size={12} />}
                    {t("printer.test")}
                  </button>
                  {!p.is_default && (
                    <button className="btn-ghost" onClick={() => setDefault(p.id)} data-testid={`printer-makedef-${p.id}`}>
                      <Star size={12} /> {t("printer.makeDefault")}
                    </button>
                  )}
                  <button className="btn-ghost text-[var(--brick)]" onClick={() => remove(p.id, p.name)} data-testid={`printer-delete-${p.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <form onSubmit={create} className="mt-5 pt-5 border-t border-[var(--ink-200)] grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="printer-add-form">
          <input
            className="input"
            placeholder={t("printer.namePh")}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            maxLength={80}
            data-testid="printer-name-input"
          />
          <input
            className="input"
            placeholder={t("printer.hostPh")}
            value={form.host}
            onChange={(e) => setForm({ ...form, host: e.target.value })}
            required
            maxLength={255}
            data-testid="printer-host-input"
          />
          <input
            className="input"
            type="number"
            placeholder={t("printer.portPh")}
            value={form.port}
            onChange={(e) => setForm({ ...form, port: e.target.value })}
            min={1}
            max={65535}
            data-testid="printer-port-input"
          />
          <input
            className="input"
            placeholder={t("printer.notePh")}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            maxLength={200}
            data-testid="printer-note-input"
          />
          <label className="flex items-center gap-2 text-sm text-[var(--ink-700)] md:col-span-2">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
              data-testid="printer-isdefault-input"
            />
            {t("printer.markDefault")}
          </label>
          <div className="md:col-span-2">
            <button type="submit" className="btn-primary" disabled={creating} data-testid="printer-create-btn">
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {t("printer.add")}
            </button>
          </div>
        </form>
      )}

      <div className="mt-5 label-tech text-[var(--ink-500)] inline-flex items-center gap-2" data-testid="printer-help">
        <WifiOff size={12} />
        {t("printer.help")}
      </div>
    </div>
  );
}
